<script lang="ts" setup>
import { ref, computed } from "vue";
import { useRouter } from 'vue-router';
import { useAuthStore } from "../../store/authStore";
import { useNotificationStore } from "../../store/notificationStore";
import { useConfigStore } from "../../store/configStore";
import NotificationDropdown from "./NotificationDropdown.vue";

const store    = useAuthStore() as any;
const notif    = useNotificationStore();
const config   = useConfigStore();
const router   = useRouter();
const menuOpen = ref(false);

const isAdmin = computed(() => store.user?.role === 'admin');

const logout = () => { store.clearSession(); router.push('/login'); };
const logwat = () => { logout(); menuOpen.value = false; };

const showFirstName = () => {
  const name = store.user?.name || "";
  return name.split(" ")[0];
};

const showHotline = () => import.meta.env.VITE_HOTLINE;

const navigate = (path: string) => {
  menuOpen.value = false;
  router.push(path);
};

if (!store.isAuthenticated) store.restoreSession();
</script>

<template>
  <header class="site-header">
    <div class="header-inner">
      <!-- Logo -->
      <router-link to="/request" class="header-logo">
        <div class="logo-wordmark">
          <span class="logo-riley">{{ config.getCompanyName }}</span><span class="logo-dot">.</span>
        </div>
        <div class="logo-divider"></div>
        <span class="logo-portal">Customer Portal</span>
      </router-link>

      <!-- Right side: unauthenticated -->
      <div v-if="!store.isAuthenticated" class="header-right">
        <router-link to="/login" class="header-nav-link">Sign in</router-link>
      </div>

      <!-- Right side: authenticated -->
      <div v-else class="header-right">
        <span class="header-hotline" v-if="showHotline()">
          <span class="hotline-label">Helpline</span>
          <span class="hotline-number">{{ showHotline() }}</span>
        </span>

        <div class="header-user">
          <span class="user-greeting">Hi, <strong>{{ showFirstName() }}</strong></span>
          <span class="user-company" v-if="store.getUser?.customer_name">{{ store.getUser?.customer_name }}</span>
        </div>

        <button class="bell-trigger" :class="{ active: notif.expanded }" @click="notif.toggle()" aria-label="Notifications">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
          <span v-if="notif.unreadCount > 0" class="bell-badge">{{ notif.unreadCount > 9 ? '9+' : notif.unreadCount }}</span>
        </button>

        <button class="menu-trigger" @click="menuOpen = !menuOpen" :class="{ active: menuOpen }">
          <span>Menu</span>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2 4l4 4 4-4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>

        <!-- Notification dropdown (anchored to the header, opened by the bell) -->
        <NotificationDropdown />
      </div>

      <!-- Dropdown menu (inside header-inner so it aligns with content) -->
      <transition name="menu-slide">
        <div v-if="menuOpen" class="nav-dropdown">
        <div class="nav-dropdown-inner">

          <!-- User pill at top -->
          <div class="nav-user-pill">
            <div class="nav-avatar">{{ showFirstName().charAt(0).toUpperCase() }}</div>
            <div class="nav-user-info">
              <div class="nav-user-name">{{ store.user?.name }}</div>
              <div class="nav-user-email">{{ store.user?.email }}</div>
            </div>
          </div>

          <div class="nav-divider"></div>

          <div class="nav-section">
            <div class="nav-section-label">Orders</div>
            <button class="nav-item" @click="navigate('/request')">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
              New Order
            </button>
          </div>

          <div class="nav-divider"></div>

          <div class="nav-section">
            <div class="nav-section-label">Transactions</div>
            <button class="nav-item" @click="navigate('/transactions/orders')">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
              Order History
            </button>
            <button class="nav-item" @click="navigate('/invoices')">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
              Invoices
            </button>
            <button class="nav-item" @click="navigate('/transactions/payments')">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
              Payments
            </button>

          </div>

          <div class="nav-divider" v-if="isAdmin"></div>

          <div class="nav-section" v-if="isAdmin">
            <div class="nav-section-label">Team</div>
            <button class="nav-item" @click="navigate('/business')">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              Users
            </button>
            <button class="nav-item" @click="navigate('/business-profile')">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
              Business Profile
            </button>
          </div>

          <div class="nav-divider"></div>

          <div class="nav-section">
            <div class="nav-section-label">Account</div>
            <button class="nav-item" @click="navigate('/profile')">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              My Profile
            </button>
            <button class="nav-item" @click="navigate('/change-password')">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              Change Password
            </button>
          </div>

          <div class="nav-divider"></div>

          <button class="nav-item nav-item--logout" @click="logwat">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            Logout
          </button>
        </div>
      </div>
      </transition>

      <div v-if="menuOpen" class="nav-backdrop" @click="menuOpen = false"></div>
    </div>
  </header>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&family=DM+Serif+Display&display=swap');

.site-header {
  position: relative;
  z-index: 100;
  background: #ffffff;
  border-bottom: 3px solid #eb1f28;
  font-family: 'DM Sans', sans-serif;
  box-shadow: 0 1px 0 rgba(0,97,235,0.06);
}

.header-inner {
  position: relative;
  max-width: 1216px;
  margin: 0 auto;
  padding: 0 10px 0 0;
  height: 62px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

/* ── Logo ──────────────────────────────── */
.header-logo {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  flex-shrink: 0;
}

.logo-wordmark {
  display: flex;
  align-items: baseline;
  gap: 1px;
}

.logo-riley {
  font-family: 'DM Serif Display', serif;
  font-size: 1.75rem;
  font-weight: 400;
  color: #0061eb;
  letter-spacing: -0.02em;
  line-height: 1;
}

.logo-dot {
  font-family: 'DM Serif Display', serif;
  font-size: 1.5rem;
  color: #eb1f28;
  line-height: 1;
}

.logo-divider {
  width: 1px;
  height: 18px;
  background: #e2e8f0;
  flex-shrink: 0;
}

.logo-portal {
  font-size: 0.78rem;
  font-weight: 500;
  color: #94a3b8;
  letter-spacing: 0.01em;
  white-space: nowrap;
}

/* ── Right side ─────────────────────────── */
.header-right {
  display: flex;
  align-items: center;
  gap: 20px;
}

.header-nav-link {
  font-size: 0.87rem;
  color: #0061eb;
  text-decoration: none;
  font-weight: 600;
}

/* ── Hotline ─────────────────────────────── */
.header-hotline {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.2;
}

.hotline-label {
  font-size: 0.62rem;
  color: #8a95a6;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
}

.hotline-number {
  font-size: 0.82rem;
  font-weight: 600;
  color: #0f1729;
}

/* ── User info ───────────────────────────── */
.header-user {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.25;
}

.user-greeting {
  font-size: 0.82rem;
  color: #94a3b8;
  font-weight: 400;
}

.user-greeting strong {
  font-weight: 700;
  color: #0061eb;
}

.user-company {
  font-size: 0.7rem;
  color: #b0bec5;
  font-weight: 400;
}

/* ── Menu trigger ────────────────────────── */
.menu-trigger {
  display: flex;
  align-items: center;
  gap: 7px;
  background: white;
  color: #0061eb;
  border: 1.5px solid #0061eb;
  border-radius: 8px;
  padding: 7px 16px;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.83rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  letter-spacing: 0.01em;
}

.menu-trigger:hover { background: #f0f6ff; }
.menu-trigger.active { background: #e8f0ff; }

/* ── Notification bell ───────────────────── */
.bell-trigger {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  background: white;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  color: #64748b;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}
.bell-trigger:hover { background: #f0f6ff; border-color: #0061eb; color: #0061eb; }
.bell-trigger.active { background: #e8f0ff; border-color: #0061eb; color: #0061eb; }

.bell-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 17px;
  height: 17px;
  padding: 0 4px;
  background: #eb1f28;
  color: white;
  border: 2px solid white;
  border-radius: 20px;
  font-size: 0.6rem;
  font-weight: 700;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.menu-trigger svg { transition: transform 0.2s; }
.menu-trigger.active svg { transform: rotate(180deg); }

/* ── Dropdown ────────────────────────────── */
.nav-dropdown {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  width: 240px;
  background: white;
  border: 1.5px solid #dce8ff;
  border-radius: 14px;
  box-shadow: 0 12px 40px rgba(0,61,160,0.14), 0 2px 8px rgba(0,97,235,0.07);
  z-index: 200;
  overflow: hidden;
}

.nav-dropdown-inner { padding: 8px; }

/* ── User pill at top ────────────────────── */
.nav-user-pill {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 10px 10px;
}

.nav-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0061eb 0%, #003a96 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  font-weight: 700;
  flex-shrink: 0;
}

.nav-user-info { min-width: 0; }
.nav-user-name {
  font-size: 0.83rem;
  font-weight: 700;
  color: #0f1729;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.nav-user-email {
  font-size: 0.72rem;
  color: #8a95a6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Sections ────────────────────────────── */
.nav-section { padding: 3px 0; }

.nav-section-label {
  font-size: 0.62rem;
  font-weight: 700;
  color: #8a95a6;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding: 4px 10px 3px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  padding: 7px 10px;
  background: none;
  border: none;
  border-radius: 8px;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.86rem;
  font-weight: 500;
  color: #4a5568;
  text-align: left;
  cursor: pointer;
  transition: background 0.1s, color 0.1s;
}

.nav-item svg { color: #8a95a6; flex-shrink: 0; }

.nav-item:hover {
  background: #f0f6ff;
  color: #0061eb;
}
.nav-item:hover svg { color: #0061eb; }

.nav-divider {
  height: 1px;
  background: #e8f0ff;
  margin: 4px 0;
}

.nav-item--logout {
  color: #eb1f28;
  font-weight: 600;
}
.nav-item--logout svg { color: #eb1f28; }
.nav-item--logout:hover {
  background: #fff0f1;
  color: #c7191f;
}
.nav-item--logout:hover svg { color: #c7191f; }

/* ── Backdrop ────────────────────────────── */
.nav-backdrop {
  position: fixed;
  inset: 0;
  z-index: 99;
}

/* ── Transition ──────────────────────────── */
.menu-slide-enter-active,
.menu-slide-leave-active {
  transition: opacity 0.15s ease, transform 0.18s cubic-bezier(0.4,0,0.2,1);
}
.menu-slide-enter-from,
.menu-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.98);
}
</style>
